import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Upload, FileSpreadsheet, Image as ImageIcon, FileText } from "lucide-react";

interface FileDropContextType {
  droppedFile: File | null;
  consumeFile: () => File | null;
}

const FileDropContext = createContext<FileDropContextType | undefined>(undefined);

export function useFileDrop() {
  const context = useContext(FileDropContext);
  if (!context) {
    throw new Error("useFileDrop must be used within a FileDropProvider");
  }
  return context;
}

export const FileDropProvider = ({ children }: { children: React.ReactNode }) => {
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Consume the dropped file (returns file and resets state)
  const consumeFile = () => {
    if (!droppedFile) return null;
    const file = droppedFile;
    setDroppedFile(null);
    return file;
  };

  useEffect(() => {
    let dragCounter = 0;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      // Ensure we are dragging a file
      if (e.dataTransfer && e.dataTransfer.types.includes("Files")) {
        dragCounter++;
        setIsDragging(true);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.types.includes("Files")) {
        dragCounter--;
        if (dragCounter <= 0) {
          setIsDragging(false);
        }
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      dragCounter = 0;

      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        const filename = file.name.toLowerCase();

        // Detect appropriate tool
        let targetPath = "";
        if (filename.endsWith(".csv")) {
          targetPath = "/csv-editor";
        } else if (filename.endsWith(".xlsx") || filename.endsWith(".xls")) {
          targetPath = "/excel-editor";
        } else if (/\.(png|jpe?g|webp|gif|svg)$/i.test(filename)) {
          targetPath = "/image-base64";
        }

        if (targetPath) {
          setDroppedFile(file);
          if (location.pathname !== targetPath) {
            navigate(targetPath);
          }
        }
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [navigate, location.pathname]);

  return (
    <FileDropContext.Provider value={{ droppedFile, consumeFile }}>
      {children}
      
      {/* Premium Full-Screen Blur Drag-and-Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 z-[999999] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-8 transition-all animate-in fade-in duration-200">
          <div className="max-w-xl w-full border-4 border-dashed border-blue-500 bg-slate-900 rounded-2xl p-12 text-center shadow-2xl flex flex-col items-center">
            <div className="w-20 h-20 bg-blue-500/10 text-blue-400 rounded-full flex items-center justify-center mb-6 animate-pulse">
              <Upload className="w-10 h-10" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">
              Drop Your File to Open Instantly!
            </h2>
            <p className="text-slate-400 text-sm mb-8 leading-relaxed">
              We'll automatically route and load your spreadsheet or image into the correct web tool. All processing happens 100% locally.
            </p>

            <div className="grid grid-cols-3 gap-4 w-full">
              <div className="p-4 bg-slate-800/50 rounded-xl flex flex-col items-center border border-slate-700/50">
                <FileText className="w-6 h-6 text-red-400 mb-2" />
                <span className="text-[11px] font-semibold text-slate-300">CSV Editor</span>
                <span className="text-[9px] text-slate-500">.csv</span>
              </div>
              
              <div className="p-4 bg-slate-800/50 rounded-xl flex flex-col items-center border border-slate-700/50">
                <FileSpreadsheet className="w-6 h-6 text-green-400 mb-2" />
                <span className="text-[11px] font-semibold text-slate-300">Excel Editor</span>
                <span className="text-[9px] text-slate-500">.xlsx, .xls</span>
              </div>

              <div className="p-4 bg-slate-800/50 rounded-xl flex flex-col items-center border border-slate-700/50">
                <ImageIcon className="w-6 h-6 text-blue-400 mb-2" />
                <span className="text-[11px] font-semibold text-slate-300">Image to Base64</span>
                <span className="text-[9px] text-slate-500">png, jpg, webp</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </FileDropContext.Provider>
  );
};
